import _ from 'lodash';
import React from 'react';

import {
  SaveScheduledJobMutation,
  ScheduledJobsTabDocument,
  ScheduledJobsTabQuery,
  usePingE1LazyQuery,
  usePingNetSuiteLazyQuery,
  useRunAllJobsMutation,
  useRunScheduledJobMutation,
  useSaveScheduledJobMutation,
  useScheduledJobsTabQuery,
  useTestPingMutation
} from 'generated/graphql';
import {
  DateTime,
  Duration
} from 'luxon';

import update from 'immutability-helper';

import { gqlCacheUtil } from 'modules';



export interface IRun {
  id: string;
  start: DateTime;
  end: DateTime | null;
  duration: Duration;
}

export interface IScheduledJob {
  id: string;
  isRunning: boolean;
  runParameters: string;
  daemonDisplayName: string;
  windowStartTime: string;
  windowEndTime: string;
  repeatPeriod: Duration;
  averageRunTime: Duration;
  lastRunTimestamp: DateTime | null;
  isActive: boolean;
  runs: IRun[];
}

export interface IPoint {
  x: string;
  y: number;
}

export interface IData {
  scheduledJobs: IScheduledJob[];

  scheduledJobRunTime: IPoint[];
}

export function useQuery() {
  const tuple = useScheduledJobsTabQuery({
    fetchPolicy: 'network-only',
    variables: {
      filter: {}
    }
  });

  return React.useMemo(
    () => {
      const data: IData = {
        scheduledJobRunTime: _.map(tuple.data?.scheduledJobs.nodes ?? [], (node): IPoint => ({
          x: node.daemon.displayName,
          y: node.averageRunTimeSeconds
        })),
        scheduledJobs: _.map(tuple.data?.scheduledJobs.nodes ?? [], (job): IScheduledJob => {
          const startTimestamp = _.first(job.runs.nodes ?? [])?.startTimestamp;
          return {
            id: job.id,
            isActive: job.isActive,
            isRunning: !_.isEmpty(job.runs.nodes) && _.isNil(_.first(job.runs.nodes)?.endTimestamp),
            daemonDisplayName: job.daemon.displayName,
            windowStartTime: job.windowStartTime ?? 'All Day',
            windowEndTime: job.windowEndTime ?? 'All Day',
            repeatPeriod: Duration.fromObject({ seconds: job.repeatPeriodSeconds }),
            averageRunTime: Duration.fromObject({ seconds: job.averageRunTimeSeconds }),
            runParameters: JSON.stringify(job.runParameters, null, 2),
            lastRunTimestamp: startTimestamp ? DateTime.fromISO(startTimestamp) : null,
            runs: _.map(job.runs.nodes, (run): IRun => ({
              id: run.id,
              start: DateTime.fromISO(run.startTimestamp),
              end: run.endTimestamp ? DateTime.fromISO(run.endTimestamp) : null,
              duration: run.endTimestamp
                ? DateTime.fromISO(run.endTimestamp).diff(DateTime.fromISO(run.startTimestamp))
                : DateTime.now().diff(DateTime.fromISO(run.startTimestamp))
            }))
          };
        })
      };
      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}



export function useTestPing() {
  const [fnMutate, tuple] = useTestPingMutation({
    refetchQueries: []
  });

  const fnWrappedMutate = React.useCallback(
    () => {
      fnMutate({
        variables: {}
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}



export function useRunScheduledJob() {
  const [fnMutate, tuple] = useRunScheduledJobMutation({
    refetchQueries: [
      {
        query: ScheduledJobsTabDocument,
        variables: {
          filter: {}
        }
      }
    ]
  });

  const fnWrappedMutate = React.useCallback(
    (scheduledJobId: string) => {
      fnMutate({
        variables: {
          scheduledJobId
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}



export function useRunAllJobs() {
  const [fnMutate, tuple] = useRunAllJobsMutation({
    refetchQueries: []
  });

  const fnWrappedMutate = React.useCallback(
    () => {
      fnMutate({
        variables: {}
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}



export function useToggleScheduledJob() {
  const [fnMutate, tuple] = useSaveScheduledJobMutation({
    updateQueries: gqlCacheUtil.genUpdateQueryMap<
      ScheduledJobsTabQuery,
      SaveScheduledJobMutation
    >(ScheduledJobsTabDocument, (prev, { mutationResult }) => {
      const index = prev.scheduledJobs.nodes.findIndex(n => n.id === mutationResult.data?.saveScheduledJob?.id);
      if (mutationResult.data?.saveScheduledJob) {
        return update(prev, {
          scheduledJobs: {
            nodes: {
              [index]: {
                $set: mutationResult.data?.saveScheduledJob
              }
            }
          }
        });
      }

      return prev;
    })
  });

  const fnWrappedMutate = React.useCallback(
    async (id: string, isActive: boolean) => {
      await fnMutate({
        variables: {
          input: {
            id,
            isActive
          }
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}



export function usePingNetSuite() {
  const [fnQuery, tuple] = usePingNetSuiteLazyQuery({
    fetchPolicy: 'network-only'
  });

  const fnWrappedQuery = React.useCallback(
    async () => {
      await fnQuery({
        variables: {}
      });
    },
    [fnQuery]
  );

  return React.useMemo(
    () => [
      fnWrappedQuery,
      {
        ...tuple,
        data: !_.isNil(tuple.data?.testNetSuiteConnection) ? (tuple.data?.testNetSuiteConnection ? 'success' : 'failure') : 'pending'
      }
    ] as const,
    [
      fnWrappedQuery,
      tuple
    ]);
}



export function usePingE1() {
  const [fnQuery, tuple] = usePingE1LazyQuery({
    fetchPolicy: 'network-only'
  });

  const fnWrappedQuery = React.useCallback(
    async () => {
      await fnQuery({
        variables: {}
      });
    },
    [fnQuery]
  );

  return React.useMemo(
    () => [
      fnWrappedQuery,
      {
        ...tuple,
        data: !_.isNil(tuple.data?.testE1Connection) ? (tuple.data?.testE1Connection ? 'success' : 'failure') : 'pending'
      }
    ] as const,
    [
      fnWrappedQuery,
      tuple
    ]);
}
